import type {
  CreateLiberalCheckoutPayload,
  CreateSubscriptionByAdminPayload,
  GetIndependentPractitionersResponse,
  Subscription,
  UpdateSubscriptionByAdminPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type SubscriptionResponse = { subscription: Subscription | null };
type SubscriptionMutationResponse = { subscription: Subscription; message: string };

/**
 * Mirrors THREE server controllers that all deal with subscription state: `apps/server/src/
 * modules/subscription` (`subscriptionController`, prefix `/subscriptions` — trial + admin
 * pro/pro-trial management + the independent-practitioners admin list) and `apps/server/src/
 * modules/liberal-subscription` (`liberalSubscriptionController`, prefix `/liberal-subscription`
 * — Stripe checkout/portal/cancel for the 2026-04 libéral pricing, 29.99€/mo or 299.99€/yr). Both
 * ultimately read/write the same `subscriptions` table (confirmed via `subscription.entity.ts`:
 * trial, admin-granted, and Stripe-liberal subscriptions are all rows there), so `getSubscription`
 * (`GET /subscriptions`) is the one canonical "my current subscription" read both apps already
 * share (web's liberal billing page and mobile's trial/paywall both call it) — `subscribeToTrial`
 * and `cancelLiberalSubscription` both invalidate its `SELF` tag.
 *
 * No RevenueCat routes here: mobile's native purchases go through the RevenueCat SDK directly,
 * and RevenueCat's webhook calls our server server-to-server (`apps/server/src/modules/webhook`)
 * — neither is a route our own apps call, so neither belongs in this RTK Query layer. Only
 * `GET /subscriptions` and `POST /subscriptions/trial`, which mobile does call directly, are
 * modeled — confirmed against `apps/mobile/src/subscription/stores/usecases/`.
 *
 * `createLiberalCheckout`/`openLiberalPortal` return a Stripe-hosted URL and carry no
 * `invalidatesTags`: actual subscription activation/cancellation happens later, asynchronously,
 * via Stripe webhook — not synchronously as a result of generating the checkout/portal link.
 *
 * `createSubscriptionByAdmin`/`updateSubscriptionByAdmin` act on an arbitrary `user_id`, not the
 * calling admin's own subscription, so they invalidate the independent-practitioners list
 * (`getIndependentPractitioners`, whose rows embed each practitioner's `subscription`) rather than
 * `SELF`.
 */
export const subscriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubscription: builder.query<Subscription | null, void>({
      query: () => ({ path: ['subscriptions'], method: 'get' }),
      transformResponse: (response: SubscriptionResponse) => response.subscription,
      providesTags: [{ type: 'Subscription', id: 'SELF' }],
    }),

    subscribeToTrial: builder.mutation<Subscription, void>({
      query: () => ({ path: ['subscriptions', 'trial'], method: 'post' }),
      transformResponse: (response: { subscription: Subscription }) => response.subscription,
      invalidatesTags: [{ type: 'Subscription', id: 'SELF' }],
    }),

    getIndependentPractitioners: builder.query<GetIndependentPractitionersResponse, void>({
      query: () => ({ path: ['subscriptions', 'admin', 'practitioners'], method: 'get' }),
      providesTags: [{ type: 'Subscription', id: 'INDEPENDENT_PRACTITIONERS_LIST' }],
    }),

    createSubscriptionByAdmin: builder.mutation<Subscription, CreateSubscriptionByAdminPayload>({
      query: (body) => ({ path: ['subscriptions', 'admin', 'create'], method: 'post', body }),
      transformResponse: (response: SubscriptionMutationResponse) => response.subscription,
      invalidatesTags: [{ type: 'Subscription', id: 'INDEPENDENT_PRACTITIONERS_LIST' }],
    }),

    updateSubscriptionByAdmin: builder.mutation<Subscription, UpdateSubscriptionByAdminPayload>({
      query: (body) => ({ path: ['subscriptions', 'admin', 'update'], method: 'put', body }),
      transformResponse: (response: SubscriptionMutationResponse) => response.subscription,
      invalidatesTags: [{ type: 'Subscription', id: 'INDEPENDENT_PRACTITIONERS_LIST' }],
    }),

    createLiberalCheckout: builder.mutation<string, CreateLiberalCheckoutPayload>({
      query: (body) => ({ path: ['liberal-subscription', 'checkout'], method: 'post', body }),
      transformResponse: (response: { checkout_url: string; message: string }) => response.checkout_url,
    }),

    openLiberalPortal: builder.mutation<string, void>({
      query: () => ({ path: ['liberal-subscription', 'portal'], method: 'post' }),
      transformResponse: (response: { portal_url: string; message: string }) => response.portal_url,
    }),

    cancelLiberalSubscription: builder.mutation<void, void>({
      query: () => ({ path: ['liberal-subscription', 'cancel'], method: 'post' }),
      transformResponse: (): void => undefined,
      invalidatesTags: [{ type: 'Subscription', id: 'SELF' }],
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useSubscribeToTrialMutation,
  useGetIndependentPractitionersQuery,
  useCreateSubscriptionByAdminMutation,
  useUpdateSubscriptionByAdminMutation,
  useCreateLiberalCheckoutMutation,
  useOpenLiberalPortalMutation,
  useCancelLiberalSubscriptionMutation,
} = subscriptionApi;
